import {
  Button,
  Col,
  Container,
  Navbar,
  Overlay,
  Popover,
  Row,
} from "react-bootstrap";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  faListUl,
  faMapMarkedAlt,
  faPause,
  faPlay,
  faRedo,
  faUndo,
  faBookmark,
  faPencilAlt,
} from "@fortawesome/free-solid-svg-icons";
import { useEffect, useRef, useState } from "react";
import { useFeature } from "../contexts/FeatureFlagContext";
import { TtsState } from "../utils/TtsState";
import { useTts } from "../contexts/TtsContext";
import "./Footer.scss";
import useLiveAnnouncement from "../hooks/useLiveAnnouncement";

const BOOKMARK_NOTIFICATION_TIMEOUT_MS = 5000;
const BOOKMARK_ADDED_MESSAGE = "Bookmark added";

function renderPageLabel(props) {
  if (props.currentPrintPage) {
    return (
      <>
        <span>
          Page {props?.currentPrintPage} of {props?.pageList?.pages?.length}
        </span>
        <br />
      </>
    );
  } else {
    return <></>;
  }
}

function Footer(props) {
  let whereAmIRef = useRef(null);
  const bookmarkButtonRef = useRef(null);
  const bookmarkTimeoutRef = useRef(null);
  const tts = useTts();
  const [bookmarkAdded, setBookmarkAdded] = useState(false);
  const [whereAmIAnnouncing, announceWhereAmI, cancelWhereAmIAnnouncement] =
    useLiveAnnouncement();

  useEffect(() => () => clearTimeout(bookmarkTimeoutRef.current), []);

  useEffect(() => {
    if (props.showWhereAmI) {
      announceWhereAmI();
    } else {
      cancelWhereAmIAnnouncement();
    }
  }, [props.showWhereAmI, announceWhereAmI, cancelWhereAmIAnnouncement]);

  const handleBookmarkButtonClicked = () => {
    props.onBookmarkButtonClicked();
    setBookmarkAdded(true);
    clearTimeout(bookmarkTimeoutRef.current);
    bookmarkTimeoutRef.current = setTimeout(
      () => setBookmarkAdded(false),
      BOOKMARK_NOTIFICATION_TIMEOUT_MS,
    );
  };

  const whereAmIMessage = (
    <div className="whereAmI-location">
      {renderPageLabel(props)}
      <span>
        Section {props?.currentLocation?.start?.index + 1} of{" "}
        {props?.spine?.length}
      </span>
      <br />
      <span>
        {Math.ceil(
          (100 * props?.currentLocation?.start?.displayed?.page) /
            props?.currentLocation?.start?.displayed?.total,
        )}
        % of section
      </span>
    </div>
  );

  const whereAmIPopover = (
    <Popover id="where-am-i-popover" role="presentation">
      <Popover.Title>
        <strong>Current Location</strong>
      </Popover.Title>
      <Popover.Content>{whereAmIMessage}</Popover.Content>
    </Popover>
  );

  const bookmarkAddedPopover = (
    <Popover id="bookmark-added-popover" role="presentation">
      <Popover.Content>{BOOKMARK_ADDED_MESSAGE}</Popover.Content>
    </Popover>
  );

  const disableSkip = !tts.isActive();

  return (
    <Navbar as="footer" bg="primary">
      <div className="sr-only" aria-live="assertive" aria-atomic="true">
        {whereAmIAnnouncing ? whereAmIMessage : <></>}
      </div>
      <Container>
        <Row className="flex-grow-1">
          <Col
            xs={12}
            sm={3}
            className="d-flex justify-content-center justify-content-sm-start my-1"
          >
            <Button
              className="text-light"
              onClick={props.onNavButtonClicked}
              aria-label="Book Navigation"
              aria-haspopup="dialog"
            >
              <FontAwesomeIcon icon={faListUl} size="lg" role="presentation" />
            </Button>
          </Col>
          <Col xs={12} sm={6} className="d-flex justify-content-center my-1">
            <Button
              className="text-light"
              onClick={() => tts.skipBack()}
              disabled={disableSkip}
              aria-label="Audio skip back"
            >
              <FontAwesomeIcon icon={faUndo} size="lg" role="presentation" />
            </Button>
            <Button
              className="text-light"
              onClick={() => tts.handlePlayTts()}
              aria-label={tts.state === TtsState.Playing ? "Pause" : "Play"}
            >
              <FontAwesomeIcon
                icon={tts.state === TtsState.Playing ? faPause : faPlay}
                size="lg"
                role="presentation"
              />
            </Button>
            <Button
              className="text-light"
              onClick={() => tts.skipAhead()}
              disabled={disableSkip}
              aria-label="Audio skip ahead"
            >
              <FontAwesomeIcon icon={faRedo} size="lg" role="presentation" />
            </Button>
          </Col>
          <Col
            xs={12}
            sm={3}
            className="d-flex justify-content-center justify-content-sm-end my-1"
          >
            <Button
              ref={whereAmIRef}
              className="text-light"
              aria-label="Where am I?"
              aria-expanded={props.showWhereAmI}
              aria-controls="where-am-i-popover"
              onClick={props.onWhereAmIClicked}
              onKeyDown={(event) => {
                if (event.key === "Escape") {
                  event.preventDefault();
                  props.onWhereAmIClosed();
                }
              }}
              onBlur={props.onWhereAmIBlur}
            >
              <FontAwesomeIcon
                icon={faMapMarkedAlt}
                size="lg"
                role="presentation"
              />
            </Button>
            <Overlay
              show={props.showWhereAmI}
              target={whereAmIRef}
              placement="top"
            >
              {whereAmIPopover}
            </Overlay>
            <Button
              ref={bookmarkButtonRef}
              className="text-light"
              onClick={handleBookmarkButtonClicked}
              aria-label="Add bookmark at current location"
            >
              <FontAwesomeIcon
                icon={faBookmark}
                size="lg"
                role="presentation"
              />
            </Button>
            <Overlay
              show={bookmarkAdded}
              target={bookmarkButtonRef}
              placement="top"
              transition={false}
            >
              {bookmarkAddedPopover}
            </Overlay>
            {useFeature("annotations") && (
              <Button
                className="text-light"
                onClick={props.onNotesButtonClicked}
                aria-label="Add note at current location"
              >
                <FontAwesomeIcon
                  icon={faPencilAlt}
                  size="lg"
                  role="presentation"
                />
              </Button>
            )}
          </Col>
        </Row>
      </Container>
    </Navbar>
  );
}

export default Footer;
